import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { actUrlForState } from "@/lib/legislation";
import { ShieldCheck, AlertTriangle, ExternalLink } from "lucide-react";

/**
 * RichText. parses an AI-generated string and turns:
 * - "Annex N" / "Annexure N" → clickable that opens the document for that annex
 * - "s N" / "section N" → clickable that opens the relevant state Act on the official site
 * AND on hover shows a tooltip with the plain-English
 * excerpt of that section, so the user can verify the AI
 * isn't hallucinating.
 */
export default function RichText({ text, annexMap = {}, state = "QLD" }) {
 if (!text) return null;
 const token = typeof window !== "undefined" ? localStorage.getItem("tt_token") : null;

 const pattern = /\b(?:(Annex(?:ure)?)\s+(\d+)|(s|section)\s+(\d+[A-Z]{0,3}(?:\([0-9a-z]+\))?))\b/gi;

 const parts = [];
 let lastIdx = 0;
 let match;
 while ((match = pattern.exec(text)) !== null) {
 if (match.index > lastIdx) {
 parts.push({ kind: "text", value: text.slice(lastIdx, match.index) });
 }
 if (match[1]) {
 parts.push({ kind: "annex", n: parseInt(match[2], 10), label: `${match[1]} ${match[2]}` });
 } else {
 parts.push({ kind: "section", num: match[4], label: `${match[3]} ${match[4]}` });
 }
 lastIdx = match.index + match[0].length;
 }
 if (lastIdx < text.length) parts.push({ kind: "text", value: text.slice(lastIdx) });

 return (
 <span>
 {parts.map((p, i) => {
 if (p.kind === "text") return <React.Fragment key={i}>{p.value}</React.Fragment>;
 if (p.kind === "annex") {
 const d = annexMap[p.n];
 if (!d) {
 return (
 <span key={i} className="text-[#0D2B52] font-medium" title="Annex not yet in this case">
 {p.label}
 </span>
 );
 }
 const href = token
 ? `${API}/documents/${d.id}/download?token=${encodeURIComponent(token)}&inline=1`
 : "#";
 return (
 <a key={i} href={href} target="_blank" rel="noreferrer"
 className="text-[#1B6C62] underline underline-offset-2 decoration-[#1B6C62]/40 hover:decoration-[#1B6C62] hover:text-[#0D2B52] font-medium"
 title={d.original_name + (d.event_date ? ` · ${d.event_date.slice(0,10)}` : "")}
 data-testid={`rich-annex-${p.n}`}>
 {p.label}
 </a>
 );
 }
 // section
 return (
 <SectionLink key={i} num={p.num} label={p.label} state={state} />
 );
 })}
 </span>
 );
}

/** Inline section link that fetches the Act excerpt and shows it on hover/focus. */
function SectionLink({ num, label, state }) {
 const [open, setOpen] = useState(false);
 const [excerpt, setExcerpt] = useState(null); // null = not loaded yet
 const [loading, setLoading] = useState(false);
 const [notFound, setNotFound] = useState(false);
 const closeTimer = useRef(null);

 useEffect(() => () => clearTimeout(closeTimer.current), []);

 const ensureExcerpt = async () => {
 if (excerpt || notFound || loading) return;
 setLoading(true);
 try {
 const { data } = await axios.get(`${API}/acts/${encodeURIComponent(state)}/section/${encodeURIComponent(num)}`);
 setExcerpt(data);
 } catch (e) {
 if (e.response?.status === 404) setNotFound(true);
 } finally {
 setLoading(false);
 }
 };

 const url = actUrlForState(state) + `#section-${num}`;

 const show = () => {
 clearTimeout(closeTimer.current);
 setOpen(true);
 ensureExcerpt();
 };
 const hide = () => {
 closeTimer.current = setTimeout(() => setOpen(false), 120);
 };

 return (
 <span className="relative inline-block" onMouseEnter={show} onMouseLeave={hide}>
 <a
 href={url}
 target="_blank"
 rel="noreferrer"
 className="text-[#1B6C62] underline underline-offset-2 decoration-[#1B6C62]/40 hover:decoration-[#1B6C62] hover:text-[#0D2B52] font-medium"
 onFocus={show}
 onBlur={hide}
 data-testid={`rich-section-${num}`}
 >
 {label}
 </a>

 {open && (
 <span
 role="tooltip"
 className="absolute left-0 top-full mt-1.5 z-50 w-[320px] sm:w-[380px] bg-white border border-[#E5E4DE] shadow-lg p-3.5 text-[12.5px] font-normal text-[#1A1A1A] leading-relaxed rounded-sm not-italic"
 onMouseEnter={show}
 onMouseLeave={hide}
 data-testid={`rich-section-tooltip-${num}`}
 >
 {loading && (
 <span className="block text-[#6B7280] italic">Looking up the Act…</span>
 )}

 {notFound && (
 <span className="block">
 <span className="inline-flex items-center gap-1.5 text-[#B35D4C] font-mono text-[10px] tracking-wider uppercase mb-1.5">
 <AlertTriangle size={11} strokeWidth={1.75} /> Not in our verified excerpts
 </span>
 <span className="block text-[#333] font-serif">
 We don't have a plain-English copy of <strong>s {num}</strong> on file for {state}.
 Open the official Act to verify it exists before relying on it.
 </span>
 <a href={url} target="_blank" rel="noreferrer"
 className="mt-2 inline-flex items-center gap-1 text-[#1B6C62] underline text-[11.5px]">
 Open the {state} Act <ExternalLink size={11} strokeWidth={1.75} />
 </a>
 </span>
 )}

 {excerpt && (
 <span className="block">
 <span className="inline-flex items-center gap-1.5 text-[#1B6C62] font-mono text-[10px] tracking-wider uppercase mb-1.5">
 <ShieldCheck size={11} strokeWidth={1.75} /> Verified excerpt · s {num}
 </span>
 <span className="block font-serif text-[13px] text-[#0D2B52] mb-1">{excerpt.title}</span>
 <span className="block text-[#333]">{excerpt.text}</span>
 <span className="block mt-2 pt-2 border-t border-[#E5E4DE] text-[10.5px] text-[#6B7280] font-mono">
 {excerpt.act}
 </span>
 <a href={url} target="_blank" rel="noreferrer"
 className="mt-2 inline-flex items-center gap-1 text-[#1B6C62] underline text-[11.5px]"
 onClick={(e) => e.stopPropagation()}>
 Read the full section <ExternalLink size={11} strokeWidth={1.75} />
 </a>
 </span>
 )}
 </span>
 )}
 </span>
 );
}
